import {
  Component,
  EventEmitter,
  Input,
  Output,
  inject,
  signal,
  effect,
  ChangeDetectorRef,
  DestroyRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { AsyncPipe } from '@angular/common';
import { ButtonComponent } from '@aprova-digital/design-system';
import { map, switchMap } from 'rxjs';
import { cpf } from 'cpf-cnpj-validator';

import { isValidCnpjAlfa, normalizeCnpj } from '../../../../utils/cnpj.validator';
import { strictEmailValidator } from '../../../../utils/email.validator';

import { Types } from '../../../../../domain/types.domain';
import { AuthRepositoryInstruction } from '../../../../../builder/instructions/repositories/auth.repository.instructions';
import { UserRepositoryInstruction } from '../../../../../builder/instructions/repositories/user.repository.instructions';
import { ValidateCpfEmailServiceInstruction } from '../../../../../builder/instructions/services/validate-cpf-email.service.instruction';
import {
  IconComponent,
  InputComponent,
  RadioComponent,
  ValidationErrorData,
} from '../../../../../@ads/components';

import { AccountFormHandler } from '../../account-form.handler';

@Component({
  selector: 'app-personal-form',
  standalone: true,
  imports: [
    AsyncPipe,
    ReactiveFormsModule,
    RadioComponent,
    InputComponent,
    ButtonComponent,
    IconComponent,
  ],
  providers: [
    new AuthRepositoryInstruction(),
    new UserRepositoryInstruction(),
    new ValidateCpfEmailServiceInstruction(),
  ],
  templateUrl: './personal-form.component.html',
  styleUrl: './personal-form.component.scss',
})
export class PersonalFormComponent {
  private readonly _route = inject(ActivatedRoute);
  private readonly _accountFormHandler = inject(AccountFormHandler);
  private readonly _cdr = inject(ChangeDetectorRef);
  private readonly _destroyRef = inject(DestroyRef);

  private readonly _authRepository = inject(Types.AuthRepository);
  private readonly _validateCpfEmailService = inject(Types.ValidateCpfEmailService);

  @Input()
  public isLoading: boolean = false;

  @Input()
  public blockedFields: string[] = [];

  @Input()
  public cityConnection: string = '';

  @Output()
  public setStepValidation = new EventEmitter<boolean>();

  @Output()
  public onValidationError = new EventEmitter<ValidationErrorData>();

  @Output()
  public onValidating = new EventEmitter<boolean>();

  protected readonly config$ = this._route.data.pipe(
    switchMap(({ city }) => this._authRepository.getConfigByCityId(city.index)),
    map((config) => {
      return {
        isAllowedBusinessAccount: config.allowUseCnpj,
      };
    }),
  );

  constructor() {
    effect(() => {
      const profileData = this._accountFormHandler.getProfileInfo();
      if (profileData) {
        this.form.patchValue(profileData);
      }
    });
  }

  public loading = signal(false);
  public hasError = signal(false);
  public focusedField = signal<string | null>(null);

  public emailConfirmRules: Array<{
    label: string;
    valid: boolean;
    key: string;
  }> = [
    {
      label: 'Os e-mails precisam ser iguais.',
      valid: false,
      key: 'emailMismatch',
    },
  ];

  public form = new FormGroup({
    type: new FormControl('personal', {
      nonNullable: true,
      validators: [Validators.required, Validators.maxLength(144)],
    }),
    nome: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, Validators.maxLength(144)],
    }),
    sobrenome: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, Validators.maxLength(144)],
    }),
    email: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, strictEmailValidator],
    }),
    emailConfirmacao: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, strictEmailValidator],
    }),
    cpf: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
    telefone: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
  });

  get isPersonalAccount(): boolean {
    return this.form?.controls.type.value === 'personal';
  }

  ngOnInit(): void {
    this.form.controls.email.setValidators([
      strictEmailValidator,
      Validators.required,
    ]);

    this.form.controls.emailConfirmacao.setValidators([
      strictEmailValidator,
      Validators.required,
      this.validateEmailMatch.bind(this),
    ]);

    this.form.controls.cpf.setValidators([
      this.validateDocumentFn,
      Validators.required,
    ]);

    this.form.controls.telefone.setValidators([
      this.validatePhone,
      Validators.required,
    ]);

    this.form.valueChanges.subscribe(() => {
      this.setStepValidation.emit(this.form.valid);
      const profileData = this._accountFormHandler.getProfileInfo() ?? {};
      this._accountFormHandler.setProfileInfo({
        ...profileData,
        ...this.form.value,
      });

      if (this.form.controls.email.dirty) {
        this.form.controls.emailConfirmacao.updateValueAndValidity();
        this.validateEmailConfirmRules();
      }
    });

    // Also react to pure status changes (e.g. manual setErrors from CPF/email
    // validation) so the Continue button disables when async errors are set.
    this.form.statusChanges.subscribe(() => {
      this.setStepValidation.emit(this.form.valid);
    });

    this.form.controls.type.valueChanges.subscribe(() => {
      this.form.controls.cpf.updateValueAndValidity();
      this.setStepValidation.emit(this.form.valid);
    });

    // CNPJ alfanumérico é sempre em maiúsculas. Converte a digitação em tempo real
    // (não se aplica a CPF, que é só dígitos). `emitEvent: false` evita loop; por
    // isso a persistência (profileInfo) precisa receber o valor normalizado
    // explicitamente — senão um paste em minúsculas seria submetido minúsculo.
    this.form.controls.cpf.valueChanges
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((value) => {
        if (this.isPersonalAccount || typeof value !== 'string') return;
        const upper = value.toUpperCase();
        if (value !== upper) {
          this.form.controls.cpf.setValue(upper, { emitEvent: false });
          const profileData = this._accountFormHandler.getProfileInfo() ?? {};
          this._accountFormHandler.setProfileInfo({ ...profileData, cpf: upper });
        }
      });
  }

  isFieldBlocked(name: string): boolean {
    return this.blockedFields.includes(name);
  }

  private validateDocumentFn = (
    control: AbstractControl
  ): ValidationErrors | null => {
    if (this.isPersonalAccount) {
      return cpf.isValid(control.value) ? null : { cpf: true };
    }
    // CNPJ agora aceita o formato alfanumérico (IN RFB 2.229/2024). A lib
    // `cpf-cnpj-validator` só valida dígitos, por isso usamos `isValidCnpjAlfa`.
    return isValidCnpjAlfa(control.value) ? null : { cnpj: true };
  };

  private validatePhone = (control: AbstractControl) => {
    return control.value.length >= 10 ? null : { telefone: true };
  };

  private validateEmailMatch = (control: AbstractControl) => {
    if (!control.value) return null;

    const email = this.form.controls.email.value;
    const emailConfirmacao = control.value;

    return email === emailConfirmacao ? null : { emailMismatch: true };
  };

  showEmailConfirmTooltip(event: FocusEvent): void {
    this.focusedField.set('emailConfirmacao');
    this.validateEmailConfirmRules();
  }

  public clearFocusedField(): void {
    // Small delay to allow click events on buttons to register before the UI might shift
    setTimeout(() => {
      if (this.form.controls.emailConfirmacao.hasError('emailMismatch')) {
        return;
      }

      this.focusedField.set(null);
    }, 200);
  }

  validateEmailConfirmRules(): void {
    const email = this.form.controls.email.value;
    const emailConfirmacao = this.form.controls.emailConfirmacao.value;

    this.emailConfirmRules.forEach((rule) => {
      rule.valid = Boolean(
        email && emailConfirmacao && email === emailConfirmacao
      );
    });
  }

  async validateCpfAndEmail(): Promise<boolean> {
    if (!this.form.valid) {
      this.form.markAllAsTouched();
      return false;
    }

    // CPF: só dígitos. CNPJ: preserva letras (formato alfanumérico, IN RFB
    // 2.229/2024) — remover as letras aqui mutilaria o documento e quebraria a
    // checagem de duplicidade (cpfExists), permitindo cadastro duplicado.
    const cpfValue = this.isPersonalAccount
      ? this.form.controls.cpf.value.replace(/\D/g, '')
      : normalizeCnpj(this.form.controls.cpf.value);
    const emailValue = this.form.controls.email.value;

    if (!cpfValue || !emailValue || !this.cityConnection) {
      return true; // Skip validation if missing required data
    }

    this.onValidating.emit(true);

    try {
      const result = await this._validateCpfEmailService.handle({
        cpf: cpfValue,
        email: emailValue,
        connection: this.cityConnection,
        cityId: this._route.snapshot.data['city']['index'],
        accountType: this.form.controls.type.value === 'personal' ? 'EXTERNAL_USER' : 'INTERNAL_USER',
      });

      this.onValidating.emit(false);

      if (result.emailExists && result.cpfExists) {
        this.form.controls.email.setErrors({ emailExists: true });
        this.form.controls.cpf.setErrors({ cpfExists: true });
        this.form.controls.email.markAsTouched();
        this.form.controls.cpf.markAsTouched();
        this.onValidationError.emit({ type: 'both_exists' });
        this._cdr.markForCheck();
        return false;
      }

      if (result.emailExists) {
        this.form.controls.email.setErrors({ emailExists: true });
        this.form.controls.email.markAsTouched();
        this.onValidationError.emit({ type: 'email' });
        this._cdr.markForCheck();
        return false;
      }

      if (result.cpfExists) {
        this.form.controls.cpf.setErrors({ cpfExists: true });
        this.form.controls.cpf.markAsTouched();
        this.onValidationError.emit({ type: 'cpf' });
        this._cdr.markForCheck();
        return false;
      }

      return true;
    } catch (error) {
      this.onValidating.emit(false);
      // If validation endpoint fails, allow to proceed (backend will validate on submit)
      console.warn('CPF/Email validation failed, proceeding anyway:', error);
      return true;
    }
  }
}
